import {Component, HostListener, Inject, ViewChild} from "@angular/core";
import {ProjectAutocompleteComponent} from "../../autocomplete/components/project-autocomplete.component";
import {UntypedFormControl, ReactiveFormsModule} from "@angular/forms";
import {ProjectName} from "../../../core/models/Project";
import {TranslateService} from "@ngx-translate/core";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogContent} from "@angular/material/dialog";
import {TranslatableComponent} from "../../../core/models/TranslatableComponent";
import {ProjectService} from "../../../core/services/services/project.service";
import {MessageService} from "../../../core/services/services/message.service";
import {AlertEnum} from "../../../core/models/Enums/AlertEnum";
import {ConfirmationDialogComponent} from "../confirmation-dialog/confirmation-dialog.component";
import {MatIcon} from "@angular/material/icon";
import {CdkScrollable} from "@angular/cdk/scrolling";
import {MatFormField} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {AutocompleteRightDirective} from "../../directives/autocomplete-right.directive";

@Component({
	selector: "app-add-aggregated-project-dialog",
	templateUrl: "./add-aggregated-project-dialog.component.html",
	styleUrls: ["./add-aggregated-project-dialog.component.scss"],
	imports: [
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		MatFormField,
		MatInput,
		ReactiveFormsModule,
		MatAutocompleteTrigger,
		AutocompleteRightDirective,
		ProjectAutocompleteComponent,
	],
})
export class AddAggregatedProjectDialogComponent extends TranslatableComponent {
	@ViewChild("projectAuto", {static: false}) projectAutoComponent: ProjectAutocompleteComponent;
	addForm = new UntypedFormControl();
	selectedProjects: ProjectName[] = [];

	onlyAssignedReadOnly: boolean = false;

	constructor(
		private t: TranslateService,
		private projectService: ProjectService,
		public dialog: MatDialog,
		private messageService: MessageService,
		private dialogRef: MatDialogRef<AddAggregatedProjectDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
	) {
		super(t, "AddProjectDialogComponent");
		this.onlyAssignedReadOnly = true;
		dialogRef.addPanelClass("mep-dialog");
	}

	/**
	 * Closes the dialog
	 */
	closeDialog() {
		this.dialogRef.close();
	}

	@HostListener("window:keyup", ["$event"])
	keyEvent(event: KeyboardEvent) {
		if (event.code === "Escape") {
			this.closeDialog();
		}
	}

	onProjectSelectionChanged(project: ProjectName) {
		const index = this.selectedProjects.findIndex((p) => p.id === project.id);

		if (index === -1) {
			this.selectedProjects.push(project);
		} else {
			this.selectedProjects.splice(index, 1);
		}
	}

	onProjectSelectionRemove(project: ProjectName) {
		const index = this.selectedProjects.findIndex((p) => p.id === project.id);

		if (index !== -1) {
			this.selectedProjects.splice(index, 1);
			this.projectAutoComponent.remoteProjectRemove(project);
		}
	}

	async addProject() {
		if (this.data.isAddMode) {
			await this.addProjectLocally();
			return;
		}

		this.projectService.getChildProjects(this.data.id).subscribe(async (existingChildren) => {
			const existingIds = existingChildren.map((c) => c.id);

			const {duplicates, newProjects} = this.splitDuplicates(existingIds);

			if (!(await this.confirmDuplicatesIfAny(duplicates, newProjects))) {
				return;
			}

			newProjects.forEach((project) => {
				this.projectService.setAggregate(this.data.id, project.id).subscribe({
					next: () => {
						this.messageService.add(
							`${project.projectName} ${this.translate.instant("mep.services.employee.project-aggregate-success")}`,
							AlertEnum.SUCCESS,
						);
					},
					error: () => {
						this.messageService.add(
							`${project.projectName} ${this.translate.instant("mep.services.employee.project-aggregate-failed")}`,
							AlertEnum.DANGER,
						);
					},
				});
			});

			this.closeDialog();
			window.location.reload();
		});
	}

	private async addProjectLocally() {
		const existingIds = (this.data.existingChildren ?? []).map((c) => c.id);

		const {duplicates, newProjects} = this.splitDuplicates(existingIds);

		if (!(await this.confirmDuplicatesIfAny(duplicates, newProjects))) {
			return;
		}

		this.dialogRef.close(newProjects);
	}

	/**
	 * Splits the current selection into projects that would be duplicates (already an existing
	 * child, or the parent project itself) and genuinely new projects.
	 */
	private splitDuplicates(existingIds: (number | string)[]) {
		const duplicates = this.selectedProjects.filter((p) => existingIds.includes(p.id) || p.id === this.data.id);
		const newProjects = this.selectedProjects.filter((p) => !existingIds.includes(p.id) && p.id !== this.data.id);
		return {duplicates, newProjects};
	}

	/**
	 * Shows a confirmation dialog if duplicates were selected. Returns whether the caller should
	 * proceed with adding `newProjects` (false also covers the "nothing to add" case).
	 */
	private async confirmDuplicatesIfAny(duplicates: ProjectName[], newProjects: ProjectName[]): Promise<boolean> {
		if (newProjects.length === 0) {
			if (duplicates.length > 0) {
				const names = duplicates.map((p) => p.projectName).join(", ");
				await this.dialog
					.open(ConfirmationDialogComponent, {
						disableClose: false,
						data: `${names} ${this.translate.instant("mep.services.employee.project-aggregate-already-exists")}`,
					})
					.afterClosed()
					.toPromise();
			}
			this.closeDialog();
			return false;
		}

		if (duplicates.length > 0) {
			const names = duplicates.map((p) => p.projectName).join(", ");
			const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
				disableClose: false,
				data: `${names} ${this.translate.instant("mep.services.employee.project-aggregate-already-exists")}. ${this.translate.instant("mep.services.employee.project-aggregate-continue")}`,
			});

			const confirmed = await dialogRef.afterClosed().toPromise();
			if (!confirmed) {
				this.closeDialog();
				return false;
			}
		}

		return true;
	}
}
